import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.mediastore79.com";
const SITE_NAME = "Mediastore Niort";
const TWITTER_HANDLE = "@mediastore79";
const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/5g1iii6g_IMGP9226.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_2010016e-c6ae-4f83-80db-30e5f9c887c1/artifacts/7vmwauk8_LOGO%20MEDIASTORE.JPG";

/**
 * Composant SEO réutilisable — style apple.com.
 * Génère meta tags complets (Open Graph, Twitter Cards, Apple PWA, IA),
 * JSON-LD enrichi (LocalBusiness + Organization + areaServed 50km),
 * et balises adaptées au référencement par les moteurs IA (ChatGPT, Claude, Gemini).
 */
export const SEO = ({
  title,
  description,
  keywords,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noindex = false,
  schema = null,
  breadcrumbs = null,
  publishedTime = null,
  modifiedTime = null,
  articleAuthor = "Mediastore Niort",
}) => {
  const canonicalUrl = `${SITE_URL}${path}`;
  const fullTitle = title.includes("Mediastore") ? title : `${title} | Mediastore Niort`;
  const robotsContent = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  return (
    <Helmet>
      {/* ============== TITLE & META BASE ============== */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Mediastore" />
      <meta name="publisher" content="Mediastore Niort" />
      <meta name="copyright" content="© Mediastore Niort" />

      {/* ============== ROBOTS ============== */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="bingbot" content={robotsContent} />

      {/* ============== AI / LLM CRAWLERS (ChatGPT, Claude, Gemini, Perplexity) ============== */}
      {!noindex && <meta name="GPTBot" content="index, follow" />}
      {!noindex && <meta name="ChatGPT-User" content="index, follow" />}
      {!noindex && <meta name="ClaudeBot" content="index, follow" />}
      {!noindex && <meta name="Claude-Web" content="index, follow" />}
      {!noindex && <meta name="Anthropic-AI" content="index, follow" />}
      {!noindex && <meta name="anthropic-ai" content="index, follow" />}
      {!noindex && <meta name="Google-Extended" content="index, follow" />}
      {!noindex && <meta name="PerplexityBot" content="index, follow" />}
      {!noindex && <meta name="YouBot" content="index, follow" />}
      {!noindex && <meta name="Applebot-Extended" content="index, follow" />}
      {!noindex && <meta name="ai-content-declaration" content="Contenu rédigé par Mediastore Niort - réparateur Apple certifié n°1 à Niort et en Deux-Sèvres" />}

      {/* ============== GÉOLOCALISATION (Niort, Deux-Sèvres, rayon 50 km) ============== */}
      <meta name="geo.region" content="FR-79" />
      <meta name="geo.placename" content="Niort, Deux-Sèvres, Nouvelle-Aquitaine" />
      <meta name="geo.position" content="46.3230;-0.4640" />
      <meta name="ICBM" content="46.3230, -0.4640" />
      <meta name="DC.title" content={fullTitle} />
      <meta name="DC.creator" content="Mediastore Niort" />
      <meta name="DC.subject" content="Réparation Apple, iPhone, Mac, MacBook, iPad à Niort et Deux-Sèvres" />
      <meta name="DC.publisher" content="Mediastore Niort" />
      <meta name="DC.language" content="fr-FR" />
      <meta name="DC.coverage" content="Niort, Deux-Sèvres, Nouvelle-Aquitaine, France" />

      {/* ============== LANGUE ============== */}
      <html lang="fr" />
      <meta httpEquiv="content-language" content="fr-FR" />

      {/* ============== CANONICAL ============== */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr-FR" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* ============== OPEN GRAPH (Facebook, LinkedIn, etc. - style apple.com) ============== */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:site_name" content={SITE_NAME} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {type === "article" && <meta property="article:author" content={articleAuthor} />}
      {type === "article" && <meta property="article:publisher" content="https://www.mediastore79.com" />}
      {type === "article" && <meta property="article:section" content="Réparation Apple Niort" />}

      {/* ============== TWITTER CARDS (style apple.com) ============== */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* ============== APPLE PWA / MOBILE (style apple.com) ============== */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Mediastore" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="application-name" content="Mediastore Niort" />
      <meta name="format-detection" content="telephone=yes, email=yes, address=yes" />
      <meta name="theme-color" content="#DC2626" />
      <meta name="msapplication-TileColor" content="#DC2626" />
      <link rel="apple-touch-icon" href={LOGO_URL} />

      {/* ============== SCHEMA.ORG JSON-LD ============== */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}

      {/* ============== BREADCRUMB LIST ============== */}
      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: b.name,
              item: `${SITE_URL}${b.path}`,
            })),
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
